import { Avatar, Menu, UnstyledButton } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';

import { type AuthUser,useAuth } from '../../lib/auth-context';

/**
 * UserMenu — avatar trigger + dropdown for signed-in users in the AppHeader.
 *
 * Identity + account only. Destinations (Dashboard, Inbox, Browse, hat
 * sections, Admin) live in `<AppSidebar>`; switching hats happens via the
 * `<HatSwitcher>` at the top of the sidebar.
 *
 * Items: Profile · Settings · Verification · Billing (host) · Sign out.
 */
export function UserMenu({
  user,
  signOut,
}: {
  user: AuthUser;
  signOut: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const { capabilities } = useAuth();
  const initials = getInitials(user.displayName || user.email);

  return (
    <Menu shadow="md" width={220} position="bottom-end" withArrow>
      <Menu.Target>
        <UnstyledButton aria-label={`Account menu for ${user.displayName || user.email}`}>
          <Avatar radius="xl" size={36} color="brand.6" variant="filled">
            {initials}
          </Avatar>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{user.displayName || user.email}</Menu.Label>
        <Menu.Item onClick={() => void navigate({ to: '/profile' })}>Profile</Menu.Item>
        <Menu.Item onClick={() => void navigate({ to: '/settings/account' })}>
          Settings
        </Menu.Item>
        <Menu.Item onClick={() => void navigate({ to: '/verification' })}>
          Verification
        </Menu.Item>
        {capabilities?.isHost && (
          <Menu.Item onClick={() => void navigate({ to: '/billing' })}>Billing</Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          onClick={() => {
            void (async () => {
              await signOut();
              void navigate({ to: '/' });
            })();
          }}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '·';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return trimmed.slice(0, 1).toUpperCase();
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
